import { Box, Button, Stack, Text } from '@solvhealth/jigsaw-v1';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  ANNUAL_MEMBERSHIP_COST_STRING,
  MEMBER_TELEMED_VISIT_COST_CENTS,
  MEMBERSHIP_NAME,
  MONTHLY_MEMBERSHIP_COST_STRING,
  PREMIUM_VISIT_NAME,
  VIDEO_VISIT_MEMBER_DISCOUNT_DOLLARS,
} from '~/components/Membership/constants';
import SolvPlusLogo from '~/components/Membership/SolvPlusLogo';
import { useOnMembershipEntrypointClicked } from '~/components/Membership/utils';
import ResponsiveSlideInDialog from '~/components/SolvDesignSystem/Dialog/ResponsiveSlideInDialog';
import { analyticsTrackEvent } from '~/core/analytics';
import {
  SOLV_PLUS_UPSELL_MODAL_ACTION,
  SOLV_PLUS_UPSELL_MODAL_ACTION_DISMISS,
} from '~/core/analytics/events';
import useToggleRuntimeModal from '~/hooks/runtime/useToggleRuntimeModal';
import { centsToCurrencyStr } from '~/core/util/currency';

const Logo = styled.img.withConfig({ componentId: 'sc-1ibb05t-0' })`
  height: 25px;
  width: 117px;
  object-fit: contain;
`;

const Icon = styled.img.withConfig({ componentId: 'sc-1ibb05t-1' })`
  height: 40px;
  width: 40px;
  object-fit: contain;
`;

type Props = {
  canInitiateBooking?: boolean;
};

export const SOLV_PLUS_UPSELL_MODAL = 'solv-plus-upsell-modal';

const UpsellModal = ({ canInitiateBooking }: Props) => {
  const { t } = useTranslation(['membership']);

  const [open, toggle] = useToggleRuntimeModal(SOLV_PLUS_UPSELL_MODAL);
  const close = () => {
    toggle();
    analyticsTrackEvent(SOLV_PLUS_UPSELL_MODAL_ACTION, {
      action: SOLV_PLUS_UPSELL_MODAL_ACTION_DISMISS,
    });
  };

  const { onClick } = useOnMembershipEntrypointClicked(SOLV_PLUS_UPSELL_MODAL, {}, {});

  const handleCTAClick = () => {
    if (canInitiateBooking) {
      onClick();
    }
    close();
  };

  return (
    <ResponsiveSlideInDialog onClose={close} open={open}>
      <Stack space={3}>
        <Stack space={3} textAlign="center">
          <SolvPlusLogo />
          <Text textAlign="center" variant="h2">
            {t('membership:entrypoints.upsellModal.headline', {
              VIDEO_VISIT_MEMBER_DISCOUNT_DOLLARS,
              PREMIUM_VISIT_NAME,
            })}
          </Text>
        </Stack>
        <Stack borderBottom="solid 1px" borderColor="gray-200" pb={4} pt={1} space={3}>
          <Stack row space={2}>
            <Icon
              alt="icon representing around the clock"
              src="/images/account/solv_plus_24_7.svg"
            />
            <Stack>
              <Text variant="h4">{t('membership:entrypoints.upsellModal.reason1.title')}</Text>
              <Text muted variant="body2">
                {t('membership:entrypoints.upsellModal.reason1.description')}
              </Text>
            </Stack>
          </Stack>
          <Stack row space={2}>
            <Icon
              alt="piggy bank icon with coins being added"
              src="/images/account/solv_plus_piggy_bank.svg"
            />
            <Stack>
              <Text variant="h4">{t('membership:entrypoints.upsellModal.reason2.title')}</Text>
              <Box pb={1}>
                <Text muted variant="body2">
                  {t('membership:entrypoints.upsellModal.reason2.description', {
                    TELEMED_VISIT_COST_STRING: centsToCurrencyStr(MEMBER_TELEMED_VISIT_COST_CENTS),
                    MEMBERSHIP_NAME,
                  })}
                </Text>
              </Box>
            </Stack>
          </Stack>
        </Stack>
        <Stack space={3}>
          <Text textAlign="center" variant="h4">
            {t('membership:entrypoints.upsellModal.costSummary', {
              MONTHLY_MEMBERSHIP_COST_STRING,
              ANNUAL_MEMBERSHIP_COST_STRING,
            })}
          </Text>
          <Button onClick={handleCTAClick} size="large" variant="contained">
            {canInitiateBooking
              ? t('membership:entrypoints.upsellModal.canInitiateBookingCta')
              : t('membership:entrypoints.upsellModal.cta')}
          </Button>
          <Box pb={2}>
            <Text muted textAlign="center" variant="body2">
              {t('membership:entrypoints.upsellModal.howToSignUp')}
            </Text>
          </Box>
        </Stack>
      </Stack>
    </ResponsiveSlideInDialog>
  );
};

export default UpsellModal;
